import React, { useRef } from 'react';
import { FieldLabel } from '@puckeditor/core';
import styled from 'styled-components';

import { HelpTooltip } from './help-tooltip';

type FieldLabelWithHelpProps = {
  label: string;
  help?: string;
  helpId?: string;
  helpStyle?: React.CSSProperties;
  icon?: React.ReactNode;
  readOnly?: boolean;
  /** Keep a native <label> (click the text to use the control) for fields whose help never changes. */
  nativeLabel?: boolean;
  children: React.ReactNode;
};

/**
 * Puck draws a field label's icon slot before the label text. Help belongs after the text, so reorder
 * the label row's flex items (label text, then icons, then the read-only lock) without changing the DOM
 * tree, which keeps the field subtree stable. Selectors: this div > Puck's label root > label row > icon
 * slot (the only row child that is not the read-only lock, which Puck titles).
 */
const FieldLabelBounds = styled.div`
  min-width: 0;
  width: 100%;

  > div > div:first-child > div:not([title]) {
    order: 1;
    margin-inline-start: 0;
    margin-inline-end: 0;
  }

  > div > div:first-child > div[title] {
    order: 2;
  }
`;

/** Keep the field subtree stable when help changes; controls supply their own accessible names. */
export function FieldLabelWithHelp({
  label,
  help,
  helpId,
  helpStyle,
  icon,
  readOnly,
  nativeLabel,
  children,
}: FieldLabelWithHelpProps): JSX.Element {
  const boundsRef = useRef<HTMLDivElement>(null);
  return (
    <FieldLabelBounds ref={boundsRef}>
      <FieldLabel
        label={label}
        readOnly={readOnly}
        el={nativeLabel && !help && !icon ? 'label' : 'div'}
        icon={
          help ? (
            <>
              {icon}
              <HelpTooltip text={help} label={label} id={helpId} boundsRef={boundsRef} style={helpStyle} />
            </>
          ) : (
            icon
          )
        }
      >
        {children}
      </FieldLabel>
    </FieldLabelBounds>
  );
}
